// =============================================================================
// services/socialGraph.ts — LR4: live FOLLOW / UNFOLLOW / BLOCK / MUTE graph
//
// The single client surface for follow/unfollow + the live propagation of the
// social graph. It:
//   1. Wraps follow/unfollow REST calls and reconciles the AUTHORITATIVE counts
//      the server returns (never trusts optimistic math — count-drift guard).
//   2. Subscribes (via LR1's reconnect-safe onUserEvent) to `follow:update`,
//      `block:update`, `mute:update` on the personal room so a user's OTHER
//      devices — and the *other party* — update live with no refetch.
//   3. Propagates follow → feed: on follow it fetches the followed user's recent
//      posts and pushes them into the live feed via LR3's feedEvents.pushLocalPost
//      (we EMIT through the shared store; we never touch the feed screen). On
//      unfollow/block we don't have a live "remove" channel into home.tsx, so the
//      next feed refetch (pull-to-refresh / focus / reconnect) drops them — the
//      server feed already excludes blocked users (bidirectional) + unfollowed
//      authors in the "following" feed.
//   4. Bridges block:update into A15's moderation registry so the BLOCKED user's
//      device hides the blocker's content instantly (reverse direction).
//
// Idempotent + dedupe: every reconcile is keyed by a stable id and the per-pair
// follow state is tracked locally, so rapid toggle (follow→unfollow→follow…)
// can never double-count or go negative — the server count always wins.
// Defensive: never throws; socket offline ⇒ REST still works, reconnect fills gaps.
// =============================================================================
import { api } from './api';
import { onUserEvent, onReconnect } from './socket';
import { pushLocalPost } from './feedEvents';
import { applyServerBlock } from './moderation';

// ── Live count store (followerCount + followingCount per userId/username) ─────
// A profile screen subscribes by username; we also key by id for socket payloads.

export interface ProfileCounts {
  followerCount: number;
  followingCount: number;
  isFollowing?: boolean;
}

type CountListener = (counts: ProfileCounts) => void;

function norm(v?: string | null): string {
  return (v || '').trim().toLowerCase().replace(/^@/, '');
}

// Keyed by BOTH username and id (both normalized) → same record reference, so a
// socket payload (id-keyed) and a screen subscription (username-keyed) coincide.
const records = new Map<string, ProfileCounts>();
const listeners = new Map<string, Set<CountListener>>();

function getRecord(...keys: Array<string | undefined>): ProfileCounts | undefined {
  for (const k of keys) {
    const n = norm(k);
    if (n && records.has(n)) return records.get(n);
  }
  return undefined;
}

function setRecord(rec: ProfileCounts, ...keys: Array<string | undefined>): void {
  for (const k of keys) {
    const n = norm(k);
    if (n) records.set(n, rec);
  }
}

function emitFor(rec: ProfileCounts, ...keys: Array<string | undefined>): void {
  const seen = new Set<CountListener>();
  for (const k of keys) {
    const n = norm(k);
    const set = n && listeners.get(n);
    if (!set) continue;
    for (const fn of set) {
      if (seen.has(fn)) continue;
      seen.add(fn);
      try { fn({ ...rec }); } catch { /* a bad listener never breaks the rest */ }
    }
  }
}

/**
 * Seed/replace the known counts for a user (e.g. when a profile loads). Returns
 * the live record so the screen can subscribe and stay in sync.
 */
export function seedCounts(
  identity: { id?: string; username?: string },
  counts: ProfileCounts
): ProfileCounts {
  const existing = getRecord(identity.id, identity.username);
  const rec: ProfileCounts = existing || { followerCount: 0, followingCount: 0 };
  rec.followerCount = Math.max(0, Number(counts.followerCount) || 0);
  rec.followingCount = Math.max(0, Number(counts.followingCount) || 0);
  if (counts.isFollowing !== undefined) rec.isFollowing = counts.isFollowing;
  setRecord(rec, identity.id, identity.username);
  emitFor(rec, identity.id, identity.username);
  return rec;
}

/**
 * Subscribe to live count changes for a user (by id and/or username). Fires
 * immediately with the current record if one exists. Returns an unsubscribe fn.
 */
export function subscribeCounts(
  identity: { id?: string; username?: string },
  cb: CountListener
): () => void {
  const keys = [norm(identity.id), norm(identity.username)].filter(Boolean);
  for (const k of keys) {
    let set = listeners.get(k);
    if (!set) { set = new Set(); listeners.set(k, set); }
    set.add(cb);
  }
  const rec = getRecord(identity.id, identity.username);
  if (rec) { try { cb({ ...rec }); } catch { /* ignore */ } }
  return () => {
    for (const k of keys) listeners.get(k)?.delete(cb);
  };
}

// Apply an authoritative count from the server (socket OR REST reply). Clamps to
// ≥0 so a stale/racing decrement can never render a negative.
function applyCounts(
  identity: { id?: string; username?: string },
  patch: Partial<ProfileCounts>
): void {
  const existing = getRecord(identity.id, identity.username);
  const rec: ProfileCounts = existing || { followerCount: 0, followingCount: 0 };
  if (patch.followerCount !== undefined) rec.followerCount = Math.max(0, Number(patch.followerCount) || 0);
  if (patch.followingCount !== undefined) rec.followingCount = Math.max(0, Number(patch.followingCount) || 0);
  if (patch.isFollowing !== undefined) rec.isFollowing = patch.isFollowing;
  setRecord(rec, identity.id, identity.username);
  emitFor(rec, identity.id, identity.username);
}

// ── Follow / unfollow (REST + optimistic + authoritative reconcile) ──────────

// Track who the current user follows locally so rapid toggling is idempotent and
// we know whether to propagate posts into the feed (only on a true 0→1 follow).
const iFollow = new Set<string>(); // normalized target id/username keys
// Dedupe socket follow:update echoes by (pair + state + ts) so a replay can't
// double-apply (counts are authoritative so this is belt-and-suspenders).
const recentFollowEchoes: string[] = [];
function followEchoSeen(key: string): boolean {
  if (recentFollowEchoes.includes(key)) return true;
  recentFollowEchoes.push(key);
  if (recentFollowEchoes.length > 200) recentFollowEchoes.shift();
  return false;
}

export interface FollowResult {
  success: boolean;
  following: boolean;
  followerCount?: number; // the TARGET's follower total (authoritative)
  error?: string;
}

/**
 * Follow or unfollow `username` (accepts id too). `currentUsername` is the actor
 * so we can keep the actor's own following-count record live as well. Optimistic:
 * the caller updates UI immediately; this reconciles to the server's number and
 * rolls back on failure. Idempotent under rapid toggle.
 */
export async function setFollow(
  target: { id?: string; username: string },
  follow: boolean,
  actor?: { id?: string; username?: string }
): Promise<FollowResult> {
  const key = norm(target.id || target.username) || norm(target.username);
  const wasFollowing = iFollow.has(key) || iFollow.has(norm(target.username));

  // Optimistic local state.
  if (follow) { iFollow.add(norm(target.username)); if (norm(target.id)) iFollow.add(norm(target.id)); }
  else { iFollow.delete(norm(target.username)); if (norm(target.id)) iFollow.delete(norm(target.id)); }

  const res = await api(`/users/${encodeURIComponent(target.username)}/follow`, {
    method: follow ? 'POST' : 'DELETE',
  });

  if (!res.success) {
    // Roll back local follow state.
    if (wasFollowing) { iFollow.add(norm(target.username)); if (norm(target.id)) iFollow.add(norm(target.id)); }
    else { iFollow.delete(norm(target.username)); if (norm(target.id)) iFollow.delete(norm(target.id)); }
    return { success: false, following: wasFollowing, error: res.error };
  }

  const data: any = res.data || {};
  const followerCount = Number(data.followerCount);   // target's followers
  const followingCount = Number(data.followingCount); // actor's following

  // Reconcile authoritative counts (server wins → no drift).
  if (Number.isFinite(followerCount)) {
    applyCounts({ id: target.id || data.targetUserId, username: target.username || data.targetUsername }, {
      followerCount,
      isFollowing: follow,
    });
  }
  if (actor && Number.isFinite(followingCount)) {
    applyCounts(actor, { followingCount });
  }

  // Propagate into the feed on a TRUE new follow (0→1): pull the followed user's
  // recent posts and prepend them live via the shared feedEvents store.
  if (follow && !wasFollowing) {
    propagateFollowedPostsToFeed(target.username).catch(() => {});
  }

  return {
    success: true,
    following: follow,
    followerCount: Number.isFinite(followerCount) ? followerCount : undefined,
  };
}

/** Fetch a freshly-followed user's recent posts and push them into the live feed. */
async function propagateFollowedPostsToFeed(username: string): Promise<void> {
  const res = await api(`/users/${encodeURIComponent(username)}/posts?page=1&limit=10`);
  if (!res.success) return;
  const data: any = res.data || {};
  const posts: any[] = Array.isArray(data.posts)
    ? data.posts
    : Array.isArray(data.items)
      ? data.items
      : Array.isArray(data)
        ? data
        : [];
  // Newest last so they end up in the right order after sequential prepends.
  for (const p of [...posts].reverse()) {
    if (p?.id) pushLocalPost(p);
  }
}

/** Is the current user following this target (local optimistic truth)? */
export function amIFollowing(target: { id?: string; username?: string }): boolean {
  return iFollow.has(norm(target.id)) || iFollow.has(norm(target.username));
}

/** Seed local follow state when a profile loads (so toggles stay idempotent). */
export function seedFollowState(target: { id?: string; username?: string }, following: boolean): void {
  const ks = [norm(target.id), norm(target.username)].filter(Boolean);
  for (const k of ks) { if (following) iFollow.add(k); else iFollow.delete(k); }
}

// ── Socket wiring (once) — consumes LR1's onUserEvent / onReconnect ──────────
let started = false;
const unsubs: Array<() => void> = [];

export function startSocialGraph(): void {
  if (started) return;
  started = true;

  // follow:update → reconcile authoritative counts on every device of both users.
  unsubs.push(
    onUserEvent('follow:update', (p: any) => {
      if (!p) return;
      const echoKey = `f:${p.followerId}:${p.followingId}:${p.following ? 1 : 0}:${p.ts ?? ''}`;
      if (followEchoSeen(echoKey)) return;
      const tFollower = Number(p.targetFollowerCount);
      const aFollowing = Number(p.actorFollowingCount);
      if (Number.isFinite(tFollower)) {
        applyCounts({ id: p.followingId, username: p.followingUsername }, { followerCount: tFollower });
      }
      if (Number.isFinite(aFollowing)) {
        applyCounts({ id: p.followerId, username: p.followerUsername }, { followingCount: aFollowing });
      }
    })
  );

  // block:update → if WE are the blocked party, hide (or restore) the blocker's
  // content via A15's moderation registry (reverse direction; the actor's own
  // device already updated locally when it called blockUser/unblockUser).
  unsubs.push(
    onUserEvent('block:update', (p: any) => {
      if (!p || !p.actorId) return;
      applyServerBlock(
        { id: p.actorId, username: p.actorUsername },
        !!p.active
      );
    })
  );

  // mute:update is intentionally NOT consumed for reverse hide (mute is
  // one-directional — only the muter hides the muted). Listener kept as a no-op
  // hook point in case future product wants symmetric mute.

  // On a non-recovered reconnect we may have missed follow/block events. Counts
  // self-heal on the next profile load (seedCounts), and feed/block state heals
  // on the next refetch + server-enforced filtering — so no extra resync needed
  // here. We still re-affirm: nothing to do, but keep the hook for clarity.
  unsubs.push(onReconnect((_info: any) => { /* counts/feeds reconcile on refetch */ }));
}

export function stopSocialGraph(): void {
  unsubs.forEach((u) => { try { u(); } catch { /* ignore */ } });
  unsubs.length = 0;
  listeners.clear();
  records.clear();
  recentFollowEchoes.length = 0;
  iFollow.clear();
  started = false;
}
